import { getStriveBaseUrl, striveHeaders } from "../striveApi";
import type { DailySummaryDto, PatientSidebar } from "./complianceTypes";
import { parseDailySummary } from "./complianceSidebar";
import type { FetchPatientSidebarParams } from "./complianceApi";

export const SIDEBAR_LOAD_FAILED = "Failed to load patient sidebar";

// Its message is written for the user; other errors show SIDEBAR_LOAD_FAILED. `expected`
// failures happen for some patients, so they aren't reported as errors.
export class DailySummaryError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly expected = false
  ) {
    super(message);
    this.name = "DailySummaryError";
  }
}

// The view's 404 for a patient without a Strive user (RuneNotFoundError in
// tangerine/api/v3/compliance/daily_summary.py). Its error_code is empty, like every 404's,
// so the message is the only way to tell it from a missing route.
const NO_STRIVE_USER_MESSAGE = /^No user found for patient /;

async function errorFor(response: Response): Promise<DailySummaryError> {
  const { status } = response;
  switch (status) {
    case 401:
      // Tokens refresh on their own, so a 401 may be a bug: still reported.
      return new DailySummaryError(
        "Your session has expired, sign in again",
        status
      );
    case 403:
      return new DailySummaryError(
        "You don't have access to this patient's data",
        status,
        true
      );
    case 404: {
      const body = await response.json().catch(() => null);
      const message = body?.error?.message;
      if (typeof message === "string" && NO_STRIVE_USER_MESSAGE.test(message)) {
        return new DailySummaryError(
          "This patient has no Strive account",
          status,
          true
        );
      }
      // Probably a missing route or wrong host, which affects every patient.
      return new DailySummaryError(`${SIDEBAR_LOAD_FAILED} (404)`, status);
    }
    default:
      return new DailySummaryError(
        `${SIDEBAR_LOAD_FAILED} (${status})`,
        status
      );
  }
}

// GET /v3/compliance/daily_summary over patient-local dates.
export async function fetchDailySummary(
  params: Pick<FetchPatientSidebarParams, "patientId" | "startDate" | "endDate">
): Promise<PatientSidebar> {
  const query = new URLSearchParams({
    patient_id: params.patientId,
    start_date: params.startDate
  });
  if (params.endDate) query.set("end_date", params.endDate);
  const response = await fetch(
    `${getStriveBaseUrl()}/api/v3/compliance/daily_summary?${query}`,
    { headers: await striveHeaders() }
  );

  // 502 means every section failed. Its body still carries the data, so each section can say
  // why it's empty.
  if (response.ok || response.status === 502) {
    const body = await response.json().catch(() => null);
    if (body?.data) return parseDailySummary(body.data as DailySummaryDto);
    // The status was fine but the body wasn't, so it isn't worth showing.
    throw new DailySummaryError(SIDEBAR_LOAD_FAILED, response.status);
  }
  throw await errorFor(response);
}

export function fetchPatientSidebar(
  params: FetchPatientSidebarParams
): Promise<PatientSidebar> {
  return fetchDailySummary(params);
}
